import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Package, AlertTriangle, CheckCircle, XCircle, Search, Save, Plus, Trash2, ChevronDown, ChevronUp, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMagazzino = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStock, setFilterStock] = useState("all");
  const [filterType, setFilterType] = useState("monoporzione");
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [batches, setBatches] = useState({});
  const [newBatch, setNewBatch] = useState({ lotto: "", scadenza: "", quantita: "" });
  const [addingBatch, setAddingBatch] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editBatchData, setEditBatchData] = useState({ lotto: "", scadenza: "", quantita_disponibile: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { Authorization: `Bearer ${token}` };

      const [productsRes, categoriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/products`, { params: { active_only: false, admin_view: true, include_business: true }, headers }),
        axios.get(`${API}/categories`, { params: { active_only: false, include_business: true }, headers }),
        axios.get(`${API}/inventory/summary`, { headers })
      ]);

      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Errore nel caricamento dati:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async (productId) => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/products/${productId}/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(prev => ({ ...prev, [productId]: response.data }));
    } catch (error) {
      console.error("Errore caricamento lotti:", error);
    }
  };

  const toggleExpand = (productId) => {
    if (expandedProduct === productId) {
      setExpandedProduct(null);
    } else {
      setExpandedProduct(productId);
      fetchBatches(productId);
    }
  };

  const handleAddBatch = async (productId) => {
    if (!newBatch.lotto || !newBatch.scadenza || !newBatch.quantita) {
      toast.error("Compila tutti i campi: lotto, scadenza e quantità");
      return;
    }
    setAddingBatch(productId);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(
        `${API}/products/${productId}/batches`,
        { lotto: newBatch.lotto, scadenza: newBatch.scadenza, quantita: parseInt(newBatch.quantita) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Lotto ${newBatch.lotto} aggiunto (stock: ${response.data.new_stock})`);
      setNewBatch({ lotto: "", scadenza: "", quantita: "" });
      fetchBatches(productId);
      // Aggiorna stock nel prodotto locale
      setProducts(products.map(p => 
        p.id === productId ? { ...p, stock: response.data.new_stock, sold_out: response.data.new_stock <= 0 } : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiunta del lotto");
    } finally {
      setAddingBatch(null);
    }
  };

  const handleDeleteBatch = async (productId, batchId) => {
    if (!window.confirm("Eliminare questo lotto?")) return;
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.delete(`${API}/products/${productId}/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Lotto eliminato (stock: ${response.data.new_stock})`);
      fetchBatches(productId);
      setProducts(products.map(p => 
        p.id === productId ? { ...p, stock: response.data.new_stock, sold_out: response.data.new_stock <= 0 } : p
      ));
    } catch (error) {
      toast.error("Errore nell'eliminazione del lotto");
    }
  };

  const startEditBatch = (batch) => {
    setEditingBatch(batch.id);
    setEditBatchData({
      lotto: batch.lotto,
      scadenza: batch.scadenza,
      quantita_disponibile: String(batch.quantita_disponibile)
    });
  };

  const handleSaveEditBatch = async (productId, batchId) => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.patch(
        `${API}/products/${productId}/batches/${batchId}`,
        {
          lotto: editBatchData.lotto,
          scadenza: editBatchData.scadenza,
          quantita_disponibile: parseInt(editBatchData.quantita_disponibile)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Lotto aggiornato (stock: ${response.data.new_stock})`);
      setEditingBatch(null);
      fetchBatches(productId);
      setProducts(products.map(p => 
        p.id === productId ? { ...p, stock: response.data.new_stock, sold_out: response.data.new_stock <= 0 } : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || "N/A";
  };

  const getStockStatus = (product) => {
    const stock = product.stock || 0;
    const threshold = product.low_stock_threshold || 5;
    if (stock <= 0) return { status: "esaurito", color: "red", icon: XCircle };
    if (stock <= threshold) return { status: "scorte basse", color: "yellow", icon: AlertTriangle };
    return { status: "disponibile", color: "green", icon: CheckCircle };
  };

  const [editingStock, setEditingStock] = useState(null);
  const [editStockValue, setEditStockValue] = useState("");

  const handleQuickStockUpdate = async (productId) => {
    const newStock = parseInt(editStockValue);
    if (isNaN(newStock) || newStock < 0) {
      toast.error("Inserisci un numero valido");
      return;
    }
    try {
      const token = localStorage.getItem("admin_token");
      await axios.patch(`${API}/products/${productId}/stock`, 
        { stock: newStock },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(products.map(p => 
        p.id === productId ? { ...p, stock: newStock, sold_out: newStock <= 0 } : p
      ));
      toast.success(`Stock aggiornato a ${newStock}`);
      setEditingStock(null);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const isExpiringSoon = (scadenza) => {
    if (!scadenza) return false;
    const expiry = new Date(scadenza);
    const today = new Date();
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 5;
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || product.category_id === filterCategory;
    const isGastronomia = product.business_only === true;
    const matchesType = filterType === "monoporzione" ? !isGastronomia : isGastronomia;
    let matchesStock = true;
    if (filterStock === "out") matchesStock = (product.stock || 0) <= 0;
    else if (filterStock === "low") {
      const stock = product.stock || 0;
      const threshold = product.low_stock_threshold || 5;
      matchesStock = stock > 0 && stock <= threshold;
    } else if (filterStock === "ok") {
      const stock = product.stock || 0;
      const threshold = product.low_stock_threshold || 5;
      matchesStock = stock > threshold;
    }
    return matchesSearch && matchesCategory && matchesStock && matchesType;
  });

  const filteredCategories = categories.filter(cat => {
    const isGastronomia = cat.business_only === true;
    return filterType === "monoporzione" ? !isGastronomia : isGastronomia;
  });

  const filteredSummary = {
    total_products: products.filter(p => filterType === "monoporzione" ? !p.business_only : p.business_only === true).length,
    in_stock: products.filter(p => {
      const matchesType = filterType === "monoporzione" ? !p.business_only : p.business_only === true;
      return matchesType && (p.stock || 0) > 0;
    }).length,
    low_stock: products.filter(p => {
      const matchesType = filterType === "monoporzione" ? !p.business_only : p.business_only === true;
      const stock = p.stock || 0;
      const threshold = p.low_stock_threshold || 5;
      return matchesType && stock > 0 && stock <= threshold;
    }).length,
    out_of_stock: products.filter(p => {
      const matchesType = filterType === "monoporzione" ? !p.business_only : p.business_only === true;
      return matchesType && (p.stock || 0) <= 0;
    }).length
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-magazzino">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white" data-testid="magazzino-title">Magazzino</h1>
          <div className="flex gap-2">
            <label className="bg-yellow-500 text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-600 transition-colors cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Importa PDF
              <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append("file", file);
                try {
                  const token = localStorage.getItem("admin_token");
                  toast.info("Importazione in corso...");
                  const res = await axios.post(`${API}/admin/import-inventory-pdf`, formData, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                  });
                  const d = res.data;
                  if (d.imported > 0) {
                    toast.success(`Importati ${d.imported} lotti su ${d.total_rows} righe`);
                  }
                  if (d.skipped > 0) {
                    toast.warning(`${d.skipped} righe saltate`);
                  }
                  // Mostra dettagli in un alert
                  let msg = `Importati: ${d.imported}\nSaltati: ${d.skipped}\n\n`;
                  if (d.details?.length > 0) {
                    msg += "IMPORTATI:\n" + d.details.map(r => `  ${r.prodotto} - Lotto ${r.lotto} - Scad ${r.scadenza} - ${r.quantita} pz (${r.azione})`).join("\n");
                  }
                  if (d.skipped_details?.length > 0) {
                    msg += "\n\nSALTATI:\n" + d.skipped_details.map(r => `  ${r.descrizione}: ${r.motivo}`).join("\n");
                  }
                  alert(msg);
                  fetchProducts();
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Errore importazione PDF");
                }
                e.target.value = "";
              }} />
            </label>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("admin_token");
                  const res = await axios.post(`${API}/admin/sync-stock`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(res.data.message);
                  fetchProducts();
                } catch { toast.error("Errore sincronizzazione"); }
              }}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors"
              data-testid="sync-stock-btn"
            >
              Sincronizza Stock
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="inventory-summary">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-zinc-400 text-sm">Totale {filterType === "monoporzione" ? "Monoporzione" : "Gastronomia"}</p>
                  <p className="text-2xl font-bold text-white">{filteredSummary.total_products}</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-zinc-400 text-sm">Disponibili</p>
                  <p className="text-2xl font-bold text-green-500">{filteredSummary.in_stock}</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-zinc-400 text-sm">Scorte Basse</p>
                  <p className="text-2xl font-bold text-yellow-500">{filteredSummary.low_stock}</p>
                </div>
              </div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <XCircle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-zinc-400 text-sm">Esauriti</p>
                  <p className="text-2xl font-bold text-red-500">{filteredSummary.out_of_stock}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Type Toggle */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-white font-semibold">Tipo prodotti:</p>
            <div className="flex bg-black rounded-lg p-1">
              <button
                onClick={() => { setFilterType("monoporzione"); setFilterCategory("all"); }}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  filterType === "monoporzione" ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                Monoporzione
              </button>
              <button
                onClick={() => { setFilterType("gastronomia"); setFilterCategory("all"); }}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  filterType === "gastronomia" ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                Gastronomia
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Cerca prodotto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-black border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-green-500"
                  data-testid="search-input"
                />
              </div>
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
              data-testid="category-filter"
            >
              <option value="all">Tutte le categorie</option>
              {filteredCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <select
              value={filterStock}
              onChange={(e) => setFilterStock(e.target.value)}
              className="bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
              data-testid="stock-filter"
            >
              <option value="all">Tutti gli stati</option>
              <option value="ok">Disponibili</option>
              <option value="low">Scorte basse</option>
              <option value="out">Esauriti</option>
            </select>
          </div>
        </div>

        {/* Products List */}
        <div className="space-y-3">
          {filteredProducts.map((product) => {
            const stockStatus = getStockStatus(product);
            const StatusIcon = stockStatus.icon;
            const isExpanded = expandedProduct === product.id;
            const productBatches = batches[product.id] || [];

            return (
              <div key={product.id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`inventory-row-${product.id}`}>
                {/* Product Row */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                  onClick={() => toggleExpand(product.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{product.name}</p>
                    <p className="text-zinc-500 text-sm">{getCategoryName(product.category_id)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {editingStock === product.id ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="number"
                          min="0"
                          value={editStockValue}
                          onChange={(e) => setEditStockValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleQuickStockUpdate(product.id); if (e.key === "Escape") setEditingStock(null); }}
                          className="w-16 bg-black border border-zinc-600 rounded px-2 py-1 text-white text-sm text-center focus:outline-none focus:border-green-500"
                          autoFocus
                          data-testid={`stock-input-${product.id}`}
                        />
                        <button
                          onClick={() => handleQuickStockUpdate(product.id)}
                          className="bg-green-500 text-black px-2 py-1 rounded text-xs font-bold hover:bg-green-400"
                        >
                          <Save className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => setEditingStock(null)}
                          className="text-zinc-500 hover:text-white px-1 py-1"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span
                        onClick={(e) => { e.stopPropagation(); setEditingStock(product.id); setEditStockValue(String(product.stock || 0)); }}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium cursor-pointer hover:ring-2 hover:ring-zinc-500
                          ${stockStatus.color === 'red' ? 'bg-red-500/20 text-red-500' : ''}
                          ${stockStatus.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-500' : ''}
                          ${stockStatus.color === 'green' ? 'bg-green-500/20 text-green-500' : ''}
                        `}
                        title="Clicca per modificare stock"
                        data-testid={`stock-badge-${product.id}`}
                      >
                        <StatusIcon className="w-4 h-4" />
                        {product.stock || 0}
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                  </div>
                </div>

                {/* Expanded: Batches */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-4 bg-black/30">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4 text-green-500" />
                      Lotti caricati
                    </h4>

                    {/* Batch list */}
                    {productBatches.length > 0 ? (
                      <div className="space-y-2 mb-4">
                        {productBatches.map((batch) => (
                          <div key={batch.id} className={`rounded-lg px-4 py-2 ${
                            isExpiringSoon(batch.scadenza) ? 'bg-red-500/10 border border-red-500/30' : 'bg-zinc-900 border border-zinc-800'
                          }`}>
                            {editingBatch === batch.id ? (
                              /* Modalità modifica */
                              <div className="flex flex-col sm:flex-row items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <div className="flex-1 flex gap-2 w-full">
                                  <input
                                    type="text"
                                    value={editBatchData.lotto}
                                    onChange={(e) => setEditBatchData({ ...editBatchData, lotto: e.target.value })}
                                    className="flex-1 bg-black border border-zinc-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-green-500"
                                    placeholder="Lotto"
                                  />
                                  <input
                                    type="date"
                                    value={editBatchData.scadenza}
                                    onChange={(e) => setEditBatchData({ ...editBatchData, scadenza: e.target.value })}
                                    className="flex-1 bg-black border border-zinc-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-green-500"
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    value={editBatchData.quantita_disponibile}
                                    onChange={(e) => setEditBatchData({ ...editBatchData, quantita_disponibile: e.target.value })}
                                    className="w-20 bg-black border border-zinc-600 rounded px-2 py-1.5 text-white text-sm text-center focus:outline-none focus:border-green-500"
                                    placeholder="Qtà"
                                  />
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handleSaveEditBatch(product.id, batch.id)}
                                    className="bg-green-500 text-black px-3 py-1.5 rounded text-sm font-semibold hover:bg-green-400"
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingBatch(null)}
                                    className="bg-zinc-700 text-white px-3 py-1.5 rounded text-sm hover:bg-zinc-600"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Modalità visualizzazione */
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-6 text-sm cursor-pointer" onClick={(e) => { e.stopPropagation(); startEditBatch(batch); }} title="Clicca per modificare">
                                  <div>
                                    <span className="text-zinc-500">Lotto:</span>
                                    <span className="text-white font-semibold ml-1">{batch.lotto}</span>
                                  </div>
                                  <div>
                                    <span className="text-zinc-500">Scadenza:</span>
                                    <span className={`font-semibold ml-1 ${isExpiringSoon(batch.scadenza) ? 'text-red-400' : 'text-white'}`}>
                                      {new Date(batch.scadenza).toLocaleDateString("it-IT")}
                                    </span>
                                    {isExpiringSoon(batch.scadenza) && (
                                      <span className="text-red-400 text-xs ml-1">(in scadenza!)</span>
                                    )}
                                  </div>
                                  <div>
                                    <span className="text-zinc-500">Disp:</span>
                                    <span className="text-green-400 font-semibold ml-1">{batch.quantita_disponibile}</span>
                                    <span className="text-zinc-600 ml-1">/ {batch.quantita_iniziale}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteBatch(product.id, batch.id); }}
                                  className="text-red-400 hover:text-red-300 p-1"
                                  data-testid={`delete-batch-${batch.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-sm mb-4">Nessun lotto caricato</p>
                    )}

                    {/* Add new batch */}
                    <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3">
                      <p className="text-zinc-300 text-sm font-semibold mb-2 flex items-center gap-1">
                        <Plus className="w-4 h-4" /> Aggiungi lotto
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          placeholder="N. Lotto"
                          value={newBatch.lotto}
                          onChange={(e) => setNewBatch({ ...newBatch, lotto: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500"
                          data-testid={`batch-lotto-${product.id}`}
                        />
                        <input
                          type="date"
                          placeholder="Scadenza"
                          value={newBatch.scadenza}
                          onChange={(e) => setNewBatch({ ...newBatch, scadenza: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500"
                          data-testid={`batch-scadenza-${product.id}`}
                        />
                        <input
                          type="number"
                          min="1"
                          placeholder="Qtà"
                          value={newBatch.quantita}
                          onChange={(e) => setNewBatch({ ...newBatch, quantita: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="w-20 bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500"
                          data-testid={`batch-quantita-${product.id}`}
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAddBatch(product.id); }}
                          disabled={addingBatch === product.id}
                          className="bg-green-500 text-black px-4 py-2 rounded-lg font-semibold text-sm hover:bg-green-600 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                          data-testid={`add-batch-${product.id}`}
                        >
                          {addingBatch === product.id ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Carica
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filteredProducts.length === 0 && (
            <div className="text-center py-8 text-zinc-500 bg-zinc-900 rounded-lg">
              Nessun prodotto trovato
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMagazzino;
